/* ─────────────────────────────────────────────────────────────
   EARNINGS-NOTE UI PRIMITIVES

   Small presentational building blocks shared by the four
   earnings-note tabs (Earnings Review · Sector Context · Thesis ·
   Screen & Catalysts). Visual language follows the design mock
   (MU_Q3FY26_Earnings_Note) mapped onto this app's theme tokens,
   so everything adapts to light/dark automatically.
───────────────────────────────────────────────────────────── */

import React from "react";
import { Tooltip as MuiTooltip } from "@mui/material";
import { gradeBackground } from "../../../config/earningsNote";

/* ── Tone → colour ───────────────────────────────────────────── */
export const toneColor = (tone) =>
  ({
    pos: "var(--color-success)",
    neg: "var(--color-error)",
    warn: "var(--color-warning)",
    info: "var(--color-info, var(--color-primary))",
    neutral: "var(--color-text-primary)",
    muted: "var(--color-text-muted)",
  }[tone] ?? "var(--color-text-primary)");

/* ── Page wrapper ──────────────────────────────────────────────
   Full width — these tabs are dense (wide statement tables, 5×5 grids,
   multi-column tile rows), so the content uses whatever the viewport gives
   it rather than sitting in a centred column with dead space to the right.  */
export const NotePage = ({ children }) => (
  <div style={{ padding: "0 var(--spacing-lg) var(--spacing-2xl)", width: "100%" }}>
    {children}
  </div>
);

/* ── Section heading ─────────────────────────────────────────── */
export const SectionTitle = ({ children, caption }) => (
  <div style={{ marginTop: "var(--spacing-lg)", marginBottom: "var(--spacing-sm)" }}>
    <div
      style={{
        display: "flex",
        alignItems: "baseline",
        gap: 10,
        flexWrap: "wrap",
        borderBottom: "1px solid var(--color-border-primary)",
        paddingBottom: 6,
      }}
    >
      <h2
        style={{
          margin: 0,
          color: "var(--color-text-primary)",
          fontSize: "var(--font-size-lg)",
          fontWeight: "var(--font-weight-bold)",
        }}
      >
        {children}
      </h2>
      {caption && (
        <span style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-xs)", fontWeight: 500 }}>
          {caption}
        </span>
      )}
    </div>
  </div>
);

/* ── Card / panel ────────────────────────────────────────────── */
export const Card = ({ children, style, accent }) => (
  <div
    style={{
      backgroundColor: "var(--color-surface)",
      border: "1px solid var(--color-border-primary)",
      borderLeft: accent ? `4px solid ${accent}` : undefined,
      borderRadius: "var(--radius-lg)",
      padding: "var(--spacing-md)",
      ...style,
    }}
  >
    {children}
  </div>
);

/* ── Metric tile grid ───────────────────────────────────────────
   Rows with a null value are dropped by the caller (null rule).   */
export const TileGrid = ({ tiles = [], min = 130 }) => {
  if (!tiles.length) return null;
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`,
        gap: "var(--spacing-sm)",
      }}
    >
      {tiles.map((t) => (
        <Card key={t.label} style={{ padding: "12px 14px", minWidth: 0 }}>
          <div
            style={{
              color: "var(--color-text-muted)",
              fontSize: "var(--font-size-xs)",
              fontWeight: "var(--font-weight-semibold)",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              marginBottom: 6,
              lineHeight: 1.35,
              /* wrap rather than ellipsise — long labels like
                 "Terminal-value reliance" were being clipped */
              whiteSpace: "normal",
              overflowWrap: "anywhere",
            }}
            title={t.tip ? undefined : t.label}
          >
            {t.label}
            {t.tip && (
              <MuiTooltip title={t.tip} placement="top" arrow enterDelay={250}>
                <span style={{ marginLeft: 5, cursor: "help", opacity: 0.7 }}>ⓘ</span>
              </MuiTooltip>
            )}
          </div>
          <div
            style={{
              color: toneColor(t.tone),
              fontSize: "var(--font-size-xl)",
              fontWeight: "var(--font-weight-bold)",
              fontVariantNumeric: "tabular-nums",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={String(t.value)}
          >
            {t.value}
          </div>
          {t.hint && (
            <div
              style={{
                color: "var(--color-text-muted)",
                fontSize: "var(--font-size-xs)",
                marginTop: 4,
                lineHeight: 1.4,
                /* hints can carry long unbroken tokens, e.g.
                   "CAPM(rf=0.0465,beta=1.065,erp=0.0446)" */
                overflowWrap: "anywhere",
              }}
            >
              {t.hint}
            </div>
          )}
        </Card>
      ))}
    </div>
  );
};

/* ── Callout banner ─────────────────────────────────────────── */
export const Banner = ({ kind = "info", children, style }) => {
  const s = {
    success: { bg: "rgba(4,120,87,0.10)",  border: "rgba(4,120,87,0.35)",  text: "var(--color-success)" },
    info:    { bg: "rgba(0,150,255,0.09)", border: "rgba(0,150,255,0.32)", text: "var(--color-primary)" },
    warning: { bg: "rgba(217,119,6,0.11)", border: "rgba(217,119,6,0.40)", text: "var(--color-warning)" },
    error:   { bg: "rgba(220,38,38,0.11)", border: "rgba(220,38,38,0.38)", text: "var(--color-error)" },
    plain:   { bg: "var(--color-bg-secondary)", border: "var(--color-border-primary)", text: "var(--color-text-secondary)" },
  }[kind];
  return (
    <div
      style={{
        backgroundColor: s.bg,
        border: `1px solid ${s.border}`,
        borderLeft: `4px solid ${s.border}`,
        color: s.text,
        borderRadius: "var(--radius-md)",
        padding: "10px 14px",
        fontSize: "var(--font-size-sm)",
        fontWeight: 500,
        lineHeight: 1.55,
        ...style,
      }}
    >
      {children}
    </div>
  );
};

/* ── Small pill / chip ──────────────────────────────────────── */
export const Pill = ({ children, tone, bg, title }) => (
  <span
    title={title}
    style={{
      display: "inline-block",
      backgroundColor: bg ?? "var(--color-surface-hover)",
      color: bg ? "#FFFFFF" : toneColor(tone ?? "muted"),
      border: bg ? "none" : "1px solid var(--color-border-primary)",
      borderRadius: "var(--radius-full, 99px)",
      padding: "3px 10px",
      fontSize: "var(--font-size-xs)",
      fontWeight: "var(--font-weight-semibold)",
      letterSpacing: "0.02em",
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </span>
);

/* ── FA grade chip ─────────────────────────────────────────── */
export const GradeChip = ({ grade }) => {
  if (grade == null || grade === "" || grade === "-") return null;
  return (
    <span
      style={{
        display: "inline-block",
        backgroundColor: gradeBackground(grade),
        color: "#FFFFFF",
        borderRadius: "var(--radius-sm)",
        padding: "2px 10px",
        fontSize: "var(--font-size-sm)",
        fontWeight: "var(--font-weight-bold)",
        minWidth: 34,
        textAlign: "center",
      }}
    >
      {grade}
    </span>
  );
};

/* ══════════════════════════════════════════════════════════════
   TABLE PRIMITIVES
══════════════════════════════════════════════════════════════ */
export const Scroller = ({ children, style }) => (
  <div
    style={{
      overflowX: "auto",
      border: "1px solid var(--color-border-primary)",
      borderRadius: "var(--radius-lg)",
      backgroundColor: "var(--color-surface)",
      ...style,
    }}
  >
    {children}
  </div>
);

export const Table = ({ children }) => (
  <table
    style={{
      borderCollapse: "collapse",
      width: "100%",
      minWidth: 520,
      fontVariantNumeric: "tabular-nums",
    }}
  >
    {children}
  </table>
);

export const Th = ({ children, left = false, style }) => (
  <th
    scope="col"
    style={{
      textAlign: left ? "left" : "right",
      padding: "9px 12px",
      backgroundColor: "var(--color-bg-secondary)",
      color: "var(--color-text-muted)",
      fontSize: "var(--font-size-xs)",
      fontWeight: "var(--font-weight-semibold)",
      textTransform: "uppercase",
      letterSpacing: "0.04em",
      borderBottom: "2px solid var(--color-border-secondary)",
      whiteSpace: "nowrap",
      ...style,
    }}
  >
    {children}
  </th>
);

export const Td = ({ children, left = false, tone, bold, wrap = false, style, title }) => (
  <td
    title={title}
    style={{
      textAlign: left ? "left" : "right",
      padding: "9px 12px",
      color: toneColor(tone ?? "neutral"),
      fontSize: "var(--font-size-sm)",
      fontWeight: bold ? "var(--font-weight-bold)" : "var(--font-weight-medium)",
      borderBottom: "1px solid var(--color-border-primary)",
      whiteSpace: wrap ? "normal" : "nowrap",
      lineHeight: wrap ? 1.5 : undefined,
      ...style,
    }}
  >
    {children}
  </td>
);

/* Row header cell (left-most label column). */
export const Tl = ({ children, style }) => (
  <th
    scope="row"
    style={{
      textAlign: "left",
      padding: "9px 12px",
      color: "var(--color-text-primary)",
      fontSize: "var(--font-size-sm)",
      fontWeight: "var(--font-weight-semibold)",
      borderBottom: "1px solid var(--color-border-primary)",
      whiteSpace: "nowrap",
      ...style,
    }}
  >
    {children}
  </th>
);

/* ══════════════════════════════════════════════════════════════
   LOADING / EMPTY SHELL
══════════════════════════════════════════════════════════════ */
export const NoteSkeleton = ({ blocks = [90, 220, 320] }) => (
  <NotePage>
    <div style={{ paddingTop: "var(--spacing-md)" }}>
      {blocks.map((h, i) => (
        <div
          key={i}
          className="animate-pulse"
          style={{
            height: h,
            backgroundColor: "var(--color-surface)",
            border: "1px solid var(--color-border-primary)",
            borderRadius: "var(--radius-lg)",
            marginBottom: "var(--spacing-md)",
          }}
        />
      ))}
    </div>
  </NotePage>
);

export const NoteEmpty = ({ children }) => (
  <div
    style={{
      padding: "var(--spacing-2xl)",
      textAlign: "center",
      color: "var(--color-text-secondary)",
      fontSize: "var(--font-size-sm)",
      fontWeight: 500,
    }}
  >
    {children}
  </div>
);

/* ══════════════════════════════════════════════════════════════
   NOTE MASTHEAD / FOOTER

   The reference note gives every panel the same head and foot: a small
   uppercase eyebrow, a serif-weight title, a muted source line, and a
   double rule underneath; then a footer stating provenance and the
   not-investment-advice line.
══════════════════════════════════════════════════════════════ */
export const NoteMasthead = ({ eyebrow, title, badge, badgeColor, meta, children }) => (
  <div
    style={{
      paddingTop: "var(--spacing-sm)",
      paddingBottom: "var(--spacing-sm)",
      borderBottom: "3px double var(--color-border-secondary)",
    }}
  >
    {eyebrow && (
      <div
        style={{
          color: "var(--color-text-muted)",
          fontSize: "var(--font-size-xs)",
          fontWeight: "var(--font-weight-semibold)",
          textTransform: "uppercase",
          letterSpacing: "0.14em",
        }}
      >
        {eyebrow}
      </div>
    )}

    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginTop: 4 }}>
      <span
        style={{
          color: "var(--color-text-primary)",
          fontSize: "var(--font-size-xl)",
          fontWeight: "var(--font-weight-bold)",
        }}
      >
        {title}
      </span>
      {badge && (
        <span
          style={{
            backgroundColor: badgeColor ?? "var(--color-primary)",
            color: "#FFFFFF",
            borderRadius: "var(--radius-sm)",
            padding: "4px 10px",
            fontSize: "var(--font-size-xs)",
            fontWeight: "var(--font-weight-bold)",
            letterSpacing: "0.05em",
          }}
        >
          {badge}
        </span>
      )}
      {children}
    </div>

    {meta && (
      <div
        style={{
          marginTop: 4,
          color: "var(--color-text-muted)",
          fontSize: "var(--font-size-xs)",
          lineHeight: 1.6,
        }}
      >
        {meta}
      </div>
    )}
  </div>
);

export const NoteFooter = ({ children }) => (
  <div
    style={{
      marginTop: "var(--spacing-xl)",
      paddingTop: "var(--spacing-md)",
      borderTop: "1px solid var(--color-border-primary)",
      color: "var(--color-text-muted)",
      fontSize: "var(--font-size-xs)",
      lineHeight: 1.6,
    }}
  >
    {children}
  </div>
);

/* ══════════════════════════════════════════════════════════════
   NOTE PROVENANCE

   `note_meta` was removed from every payload on 2026-08-17 — model /
   generated-at provenance and the draft-sign-off caveat are not rendered in
   production, so the primitives that displayed them are gone with it. Any
   footer or disclaimer text is now the FE's own static copy.
══════════════════════════════════════════════════════════════ */

/* ── A prose block: heading + paragraph, as the note renders its sections ── */
export const ProseSection = ({ children }) => (
  <p
    style={{
      color: "var(--color-text-secondary)",
      fontSize: "var(--font-size-sm)",
      lineHeight: 1.75,
      margin: "var(--spacing-sm) 0",
    }}
  >
    {children}
  </p>
);

/* ── Bulleted list of {point/item, evidence/detail/quote} entries ── */
export const NoteList = ({ items = [], boldKey = "point", bodyKeys = ["evidence", "detail", "quote"] }) => {
  if (!items.length) return null;
  return (
    <ul
      style={{
        margin: "var(--spacing-sm) 0",
        paddingLeft: 20,
        color: "var(--color-text-secondary)",
        fontSize: "var(--font-size-sm)",
        lineHeight: 1.75,
      }}
    >
      {items.map((it, i) => {
        if (typeof it === "string") return <li key={i} style={{ margin: "6px 0" }}>{it}</li>;
        const head = it[boldKey] ?? it.item ?? it.name ?? null;
        const bodyKey = bodyKeys.find((k) => it[k]);
        const body = bodyKey ? it[bodyKey] : null;
        const isQuote = bodyKey === "quote";
        return (
          <li key={i} style={{ margin: "6px 0" }}>
            {head && (
              <strong style={{ color: "var(--color-text-primary)" }}>{head}</strong>
            )}
            {head && body ? " " : ""}
            {body && (
              <span style={isQuote ? { fontStyle: "italic" } : undefined}>
                {isQuote ? `“${body}”` : body}
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
};
